import type { AdminTeamScope, AdminTeamTournament } from "./api";
import { formatDate } from "./dateFormat";

// Equipo del organizador (Eduardo 2026-09-29): cada miembro entra a "Todos
// mis torneos (también los que crees después)" o "Solo estos torneos". Aquí
// vive la lógica pura de la pantalla (textos, orden, búsqueda, resumen).

/** Estado del torneo en la lista de Equipo. Cerrado y archivado se
 * distinguen: el cerrado sigue visible pero en solo lectura. */
export function teamTournamentStatusLabel(status: string | null | undefined): string {
  switch ((status ?? "").toLowerCase()) {
    case "draft":
      return "Borrador";
    case "active":
      return "Activo";
    case "closed":
      return "Cerrado · solo lectura";
    case "archived":
      return "Archivado";
    default:
      return "";
  }
}

/** Cerrados y archivados: se pueden elegir, pero van al final y marcados. */
export function isClosedLike(status: string | null | undefined): boolean {
  const value = (status ?? "").toLowerCase();
  return value === "closed" || value === "archived";
}

/** Abiertos primero (en el orden recibido: más recientes arriba); cerrados
 * y archivados al final. No muta la lista original. */
export function sortTeamTournaments<T extends Pick<AdminTeamTournament, "status">>(tournaments: T[]): T[] {
  const open = tournaments.filter((t) => !isClosedLike(t.status));
  const closed = tournaments.filter((t) => isClosedLike(t.status));
  return [...open, ...closed];
}

function normalizeText(value: string | null | undefined): string {
  return (value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim();
}

/** Búsqueda por nombre sin acentos ni mayúsculas. */
export function filterTeamTournaments<T extends Pick<AdminTeamTournament, "name">>(tournaments: T[], query: string): T[] {
  const needle = normalizeText(query);
  if (!needle) return tournaments;
  return tournaments.filter((t) => normalizeText(t.name).includes(needle));
}

/** Fechas del torneo para distinguirlo en la lista ("03/10/2026 – 05/10/2026"). */
export function teamTournamentDates(tournament: Pick<AdminTeamTournament, "start_date" | "end_date">): string {
  const start = tournament.start_date ? formatDate(tournament.start_date) : "";
  const end = tournament.end_date ? formatDate(tournament.end_date) : "";
  if (start && end) return start === end ? start : `${start} – ${end}`;
  if (start) return `Desde ${start}`;
  if (end) return `Hasta ${end}`;
  return "Sin fechas";
}

/** "A", "A y B", "A, B y C", "A, B, C y 2 más". */
export function joinTournamentNames(names: string[], max = 3): string {
  if (names.length === 0) return "";
  if (names.length <= max) {
    if (names.length === 1) return names[0];
    return `${names.slice(0, -1).join(", ")} y ${names[names.length - 1]}`;
  }
  return `${names.slice(0, max).join(", ")} y ${names.length - max} más`;
}

function torneosText(count: number): string {
  return count === 1 ? "1 torneo" : `${count} torneos`;
}

/** Resumen del alcance de un miembro para la tabla de Miembros. */
export function teamScopeSummary(
  member: { scope: AdminTeamScope; tournament_ids: number[] },
  tournaments: AdminTeamTournament[],
): string {
  const ordered = sortTeamTournaments(tournaments);
  const assigned = new Set(member.tournament_ids);
  if (member.scope !== "selected") {
    // "Todos", salvo los torneos de donde se le quitó a mano (Equipo del torneo).
    const missing = ordered.filter((t) => !assigned.has(t.id)).map((t) => t.name);
    if (missing.length === 0) return "Todos tus torneos";
    return `Todos tus torneos, salvo ${joinTournamentNames(missing, 2)}`;
  }
  const chosen = ordered.filter((t) => assigned.has(t.id)).map((t) => t.name);
  if (chosen.length === 0) return "Ningún torneo";
  return `${torneosText(chosen.length)}: ${joinTournamentNames(chosen)}`;
}

/** A dónde entra, para los mensajes tras guardar. */
export function teamScopeReach(scope: AdminTeamScope, count: number): string {
  if (scope === "all") return "en todos tus torneos, también en los que crees después";
  return `solo en ${torneosText(count)}`;
}

/** "Solo estos torneos" necesita al menos uno (el backend también lo exige). */
export function teamScopeSelectionError(
  scope: AdminTeamScope,
  selectedIds: number[],
  context: "add" | "edit",
): string | null {
  if (scope !== "selected" || selectedIds.length > 0) return null;
  return context === "add"
    ? "Elige al menos un torneo o marca «Todos mis torneos»."
    : "Elige al menos un torneo. Para quitarle el acceso a todos tus torneos, usa «Quitar».";
}

/** Conteo para el contador de la lista: "Ninguno elegido", "1 elegido", "3 elegidos". */
export function chosenCountLabel(count: number): string {
  if (count === 0) return "Ninguno elegido";
  return count === 1 ? "1 elegido" : `${count} elegidos`;
}

/** Al elegir un sub-usuario que ya ayudaba torneo por torneo, la precarga
 * refleja SOLO a esa persona (no a quien se eligió antes, revisión
 * 2026-09-29): si no estaba en todos, se propone "Solo estos torneos" con
 * sus torneos, para no ampliarle el acceso sin querer; si estaba en todos
 * (o en ninguno), "Todos mis torneos". */
export function scopeForExistingSubUser(
  subUserTournamentIds: number[],
  tournaments: AdminTeamTournament[],
): { scope: AdminTeamScope; selectedIds: number[] } {
  const owned = new Set(tournaments.map((t) => t.id));
  const selectedIds = subUserTournamentIds.filter((id) => owned.has(id));
  if (selectedIds.length > 0 && selectedIds.length < owned.size) {
    return { scope: "selected", selectedIds };
  }
  return { scope: "all", selectedIds };
}

/** Torneos del organizador donde el miembro NO está hoy (en el orden de la
 * lista). Con alcance "todos" son los que se le quitaron desde el Equipo de
 * cada torneo. */
export function teamScopeMissing(
  member: { tournament_ids: number[] },
  tournaments: AdminTeamTournament[],
): AdminTeamTournament[] {
  const assigned = new Set(member.tournament_ids);
  return sortTeamTournaments(tournaments).filter((t) => !assigned.has(t.id));
}

function sameIds(a: number[], b: number[]): boolean {
  const left = new Set(a);
  const right = new Set(b);
  if (left.size !== right.size) return false;
  for (const id of left) if (!right.has(id)) return false;
  return true;
}

/** ¿Hay algo que guardar en «Cambiar torneos»? Un miembro con "todos" que
 * vuelve a guardar "todos" NO cambia nada, salvo que el organizador marque
 * expresamente que regrese a los torneos de donde lo quitó
 * (restoreMissing): guardar "todos" lo vuelve a meter ahí. */
export function isTeamScopeDirty(
  member: { scope: AdminTeamScope; tournament_ids: number[] },
  value: { scope: AdminTeamScope; selectedIds: number[] },
  tournaments: AdminTeamTournament[],
  restoreMissing: boolean,
): boolean {
  const owned = new Set(tournaments.map((t) => t.id));
  if (value.scope !== member.scope) return true;
  if (value.scope === "selected") {
    return !sameIds(
      value.selectedIds.filter((id) => owned.has(id)),
      member.tournament_ids.filter((id) => owned.has(id)),
    );
  }
  return restoreMissing && teamScopeMissing(member, tournaments).length > 0;
}
